import { Component, ViewChild } from '@angular/core';
import { ICellEditorParams } from 'ag-grid-community';
import { ICellEditorAngularComp } from 'ag-grid-angular';
import { CurrencyPipe } from '@angular/common';
import { select, Store } from '@ngrx/store';
import { selectCostBasis, selectPositionLotTableData } from '../../../create-request/reducers';
import { take, takeUntil } from 'rxjs/operators';
import { Subject, Observable } from 'rxjs';
import { InternalTransferActions } from '../../../create-request/actions';
import { positionConstants } from '../../utilities/Constants';
import { TooltipPosition } from '@angular/material/tooltip';
import { selectViewInContext } from '../../reducers';

@Component({
  standalone: false,
    selector: 'mm-position-text-input-editor',
    templateUrl: './table-text-position-input-field.component.html',
    styleUrls: ['./table-text-position-input-field.component.scss']
})
export class InputFieldPositionComponent implements ICellEditorAngularComp {
    constructor(private store: Store, private currencyPipe: CurrencyPipe) { }
    @ViewChild('inputShares') textInput: any;
    @ViewChild('tooltip') tooltip: any;
    params: ICellEditorParams;
    paramsString: string;
    showError: boolean;
    toolTipError: null | string | undefined = '';
    currentValue: null | string | undefined = '';
    currentTextInput: string;
    showIcon: boolean = false;
    createRequestStep$: Observable<string>;
    position: TooltipPosition = 'after';
    destroy$: Subject<boolean> = new Subject<boolean>();
    costBasisMethod: string | undefined;

    ngOnInit(): void {
        this.createRequestStep$ = this.store.select(selectViewInContext);
        this.store.select(selectCostBasis).pipe(takeUntil(this.destroy$)).subscribe(info => this.costBasisMethod = info);

    }

    agInit(params: ICellEditorParams): void {
        // do not run agInit if backspace or delete key pressed
        if (params.eventKey !== 'Backspace' && params.eventKey !== "Delete") {
            this.params = params;
            this.paramsString = (Number(params.value) !== 0)? Number(params.value).toLocaleString("en-us", { style: 'decimal', maximumFractionDigits: 20}):'';
            const nodeCostBasis: string = this.getNodeCostBasis;
            if(nodeCostBasis !== 'None'){
                this.clearLotsToMove();
            }
            const sharesToMoveString: string = this.positionLevelData?.['Shares to move'];
            this.setPositionLevelData('Shares to move', this.removeTrailingZeroDecimal(sharesToMoveString));
            const sharesToMove = parseFloat(sharesToMoveString);
            const availbleShares = parseFloat(this.positionLevelData?.['Available shares']?.replaceAll(',', ''));
            const isDecimal = (sharesToMove - Math.floor(sharesToMove)) !== 0;
            const isDecimalAvailble = (availbleShares) - Math.floor(availbleShares) !== 0;
            const quantity = this.positionLevelData?.Quantity;
    
            if (!isDecimalAvailble && isDecimal && sharesToMoveString !== '') {
                this.setUpWholeNumberError();
            }
            else if (isDecimalAvailble && isDecimal && availbleShares.toString().split('.')[1]?.length < sharesToMoveString?.toString().split('.')[1]?.length) {
                this.setUpPositionLevelFractionalError();
            }
            else if (sharesToMove > availbleShares) {
                this.setUpMinimumError();
            }
            else if (sharesToMove !== 0 && sharesToMove !== availbleShares && 
                (sharesToMove < quantity?.selectedQuantityMinimum
                    || (((sharesToMove) % (parseFloat(quantity?.selectedQuantityMinimumDenomination))) > .001 && sharesToMoveString !== ''))) {
                this.setUpDenominationError();
            }
            else {
                this.updateStorePosition(this.positionLevelData?.['Symbol'], sharesToMoveString, this.positionLevelData?.['nodeCostBasis'])
                this.clearErrors();
            }
        }
        

    }

    get isNotManualFlow() {
        let displayTextBox = this.shouldTextBoxRender(this.positionLevelData.restrictions?.isRestricted, this.positionLevelData?.restrictions?.restrictionText)
        return this.costBasisMethod !== 'None' && displayTextBox;
    }

    clearLotsToMove() {
        this.store.pipe(select(selectPositionLotTableData), take(1)).subscribe((rowData) => {
            let positionData = rowData.map(position => {
                    return {
                        ...position,
                        'lotDetail': position['lotDetail'].map((lot: any) => {
                            return {
                                ...lot,
                                'lotsToMove': ''
                            }
                        })
                    };
            });
            this.store.dispatch(InternalTransferActions.updateRowData({ response: positionData }));
        });
    }

    onchange(){
        this.textInput.nativeElement.value= parseFloat(this.textInput.nativeElement.value);
        if (this.isNotNumber(this.getValue())){
            this.textInput.nativeElement.value = "";
        }
        this.params.node.setDataValue('Shares to move', this.textInput.nativeElement.value);
    }

    isNotNumber(param: any): boolean{
        return (param && isNaN(Number(param)));
    }

    isCancelAfterEnd(): boolean {
        this.onchange();
        return false;
    }

    shouldTextBoxRender(isRestricted: boolean, restrictionText: string): boolean {
        return this.costBasisMethod !== 'None' && (!isRestricted || restrictionText === positionConstants.positionMismatchRestriction || restrictionText.startsWith('DUPLICATE'));
    }

    get positionLevelData() {
        return this.params?.data;
    }

    get getNodeCostBasis() {
        return this.positionLevelData['nodeCostBasis']
    }

    setPositionLevelData(colKey: string, value: string) {
        this.params.data[colKey] = this.params?.data != null ? value : '';
    }

    afterGuiAttached() {
        this.textInput?.nativeElement?.focus();
    }

    getValue() {
        return this.textInput?.nativeElement?.value;
    }

    onKeyPress(event: any) {
        if (!isNumeric(event)) {
            event.preventDefault();
        }
        function isNumeric(event: any) {
            return !!/\d/.test(event.key) || !!/\./.test(event.key);
        }
    }

    onkeydown(event: KeyboardEvent) {
        if(event.code === 'Enter' || event.code === 'Tab') {
            this.onchange();
        } else if (event.code === 'Backspace') {
            let selection = document.getSelection();
            let selectionString = selection?.toString() ?? '';
            let inputString = this.getValue()?.toString() ?? '';
            // for some reason, backspace only deletes one char when dragging to select all (selecting a few chars with backspace works fine)
            // this code deletes the whole string
            if (selectionString.length > 0 && selectionString.length === inputString.length) {
                event.stopPropagation();
                this.textInput.nativeElement.value = '';
                selection?.removeRange(selection.getRangeAt(0));
                this.onchange();
                this.agInit(this.params);
            }
        }
    }

    updateStorePosition(symbolName: string, sharesToMove: string, costBasis: string) {
        this.store.pipe(select(selectPositionLotTableData), take(1)).subscribe((rowData) => {
            let positionData = rowData.map(position => {
                if (position['Symbol'] === symbolName) {
                    return {
                        ...position,
                        'Shares to move': sharesToMove,
                        'nodeCostBasis': costBasis
                    };
                }
                else {
                    return {
                        ...position,
                        'nodeCostBasis': costBasis
                    };
                }

            });
            this.store.dispatch(InternalTransferActions.updateRowData({ response: positionData }));
        });
    }


    setUpWholeNumberError() {
        this.params?.node?.setRowHeight(positionConstants.tableWholeNumberErrorRowHeight);
        setTimeout(() => {
            this.params?.api?.onRowHeightChanged();
        }, 0);
        this.toolTipError = this.getNodeCostBasis !== 'None' ? "Fractional shares cannot be transferred." : "Quantity of all cost basis lots including any fractional shares must equal quantity being transferred.";
        this.currentValue = "Enter a whole number";
        this.showIcon = true;
    }

    setUpPositionLevelFractionalError() {
        this.params?.node?.setRowHeight(positionConstants.tablePositionFractionalRowHeight);
        setTimeout(() => {
            this.params?.api?.onRowHeightChanged();
        }, 0);
        this.currentValue = "Fractional quantity entered must equal fractional positions held.";
        this.showIcon = false;
    }


    setUpMinimumError() {
        this.params?.node?.setRowHeight(positionConstants.tableMinimumErrorRowHeight);
        setTimeout(() => {
            this.params?.api?.onRowHeightChanged();
        }, 0);
        this.currentValue = "Enter a quantity equal to or less than available quantity.";
        this.showIcon = false;
    }

    setUpDenominationError() {
        this.params?.node?.setRowHeight(positionConstants.tableDenominationErrorRowHeight);
        setTimeout(() => {
            this.params?.api?.onRowHeightChanged();
        }, 0);
        this.currentValue = "Enter a multiple of " + this.params.data.Quantity.selectedQuantityMinimumDenomination + " (minimum of " + this.params.data.Quantity.selectedQuantityMinimum + ").";
        this.toolTipError = "Some securities can only be traded in the specific multiples or quantities shown.";
        this.showIcon = true;
    }

    clearErrors() {
        this.currentValue = "";
        this.showIcon = false;
        this.params?.node?.setRowHeight(positionConstants.tableRowHeight);
        setTimeout(() => {
            this.params?.api?.onRowHeightChanged();
        }, 0);
    }

    removeTrailingZeroDecimal(userEnteredNum: any) {
        let checkDecimal = userEnteredNum?.toString().split('.');
        while (checkDecimal[1]?.substr(-1) === '0') {
            checkDecimal[1] = checkDecimal[1].slice(0, -1);
        }
        return checkDecimal[1]?.length ? checkDecimal[0] + '.' + checkDecimal[1] : checkDecimal[0];
    }
}